import AVAILABILITIES_ACTIONS from './availabilities.constants.js';
import { get } from 'lodash';

export const INITIAL_STATE = {
  local: [],
  availabilities: [],
  deleted: [],
  bookings: [],
  request: {},
  loading: false,
  createSuccess: false,
  error: false
};

const availabilityKey = availability =>
  `${availability.availabilityId}:${availability.eventId}`;

const mergeFetchedAvailabilities = (state, fetched) => {
  const deletedIds = new Set(state.deleted.map(a => a.availabilityId));
  const dirtyKeys = new Set(
    state.availabilities.filter(a => a.dirty).map(availabilityKey)
  );
  const incoming = fetched.filter(
    a => !deletedIds.has(a.availabilityId) && !dirtyKeys.has(availabilityKey(a))
  );
  const incomingKeys = new Set(incoming.map(availabilityKey));
  return [
    ...state.availabilities.filter(a => !incomingKeys.has(availabilityKey(a))),
    ...incoming
  ];
};

const mergeFetchedBookings = (existing, fetched) => {
  const byId = new Map(existing.map(b => [b.bookingId, b]));
  fetched.forEach(booking => {
    const previous = byId.get(booking.bookingId);
    const events = new Map(
      [...(previous?.events || []), ...(booking.events || [])].map(e => [
        e.eventId,
        e
      ])
    );
    byId.set(booking.bookingId, { ...booking, events: [...events.values()] });
  });
  return [...byId.values()];
};

export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case AVAILABILITIES_ACTIONS.ADD_LOCAL_AVAILABILITY:
      return { ...state, local: [...state.local, action.payload] };
    case AVAILABILITIES_ACTIONS.UPDATE_LOCAL_AVAILABILITY:
      return {
        ...state,
        local: state.local.map(event => {
          if (event.availabilityId !== action.payload.availabilityId) {
            return event;
          }
          return {
            ...event,
            ...action.payload.durationDetails
          };
        }),
        availabilities: state.availabilities.map(availability => {
          if (
            availability.availabilityId !==
            parseInt(action.payload.availabilityId)
          ) {
            return availability;
          }
          return {
            ...availability,
            ...action.payload.durationDetails,
            dirty: true
          };
        })
      };
    case AVAILABILITIES_ACTIONS.DELETE_LOCAL_AVAILABILITY:
      const availabilityId = parseInt(action.payload.availabilityId);
      const found = state.availabilities.find(
        availability => availability.availabilityId === availabilityId
      );
      return {
        ...state,
        local: [
          ...state.local.filter(
            event => event.availabilityId !== action.payload.availabilityId
          )
        ],
        availabilities: [
          ...state.availabilities.filter(
            availability => availability.availabilityId !== availabilityId
          )
        ],
        deleted: [...state.deleted, found].filter(
          availability => availability !== undefined
        )
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITY_REQUEST:
      return {
        ...state,
        loading: action.type,
        error: false
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITY_REQUEST_SUCCESS:
      return {
        ...state,
        request: get(action, 'result.data', {}),
        loading: false
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITY_REQUEST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITIES:
      return {
        ...state,
        loading: action.type,
        error: false
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITIES_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        availabilities: mergeFetchedAvailabilities(
          state,
          get(action, 'result.data', [])
        )
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error
      };
    case AVAILABILITIES_ACTIONS.CREATE_AVAILABILITIES:
      return {
        ...state,
        loading: false,
        creating: action.type
      };
    case AVAILABILITIES_ACTIONS.CREATE_AVAILABILITIES_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        createSuccess: action.type,
        creating: false,
        local: [],
        availabilities: [],
        deleted: []
      };
    case AVAILABILITIES_ACTIONS.CREATE_AVAILABILITIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
        createSuccess: false,
        creating: false,
        ...(Array.isArray(get(action, 'error.errors')) && {
          local: [],
          availabilities: [],
          deleted: []
        })
      };
    case AVAILABILITIES_ACTIONS.LOG_CREATE_AVAILABILITIES_SUCCESS:
      return {
        ...state,
        createSuccess: false
      };
    case AVAILABILITIES_ACTIONS.LOG_CREATE_AVAILABILITIES_FAILURE:
      return {
        ...state,
        createSuccess: false
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITIES_BOOKINGS:
      return {
        ...state,
        loading: action.type,
        error: false
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITIES_BOOKINGS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        bookings: mergeFetchedBookings(
          state.bookings,
          get(action, 'result.data', [])
        )
      };
    case AVAILABILITIES_ACTIONS.GET_AVAILABILITIES_BOOKINGS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error
      };
    default:
      return state;
  }
};
